import * as Sentry from "@sentry/react";
import {useContext, useEffect} from "react";
import {env} from "../config";
import {AuthContext} from "./AuthProvider";
import {FeatureFlagContext} from "./FeatureFlagProvider";
import log from "loglevel";

type SentryProviderProps = {
  children: JSX.Element[] | JSX.Element;
};

// Flag to track if Sentry has been initialized
let sentryInitialized = false;

const SentryProvider = ({children}: SentryProviderProps) => {
  const {featureFlagsInitialized, sentryEnabled} = useContext(FeatureFlagContext);
  const {user, tenant, isAdmin, isSuperUser, tenantId, isAuthenticated} = useContext(AuthContext);

  // Initialize Sentry based on feature flag
  useEffect(() => {
    if (featureFlagsInitialized && sentryEnabled && !sentryInitialized) {
      const isProd = env.name === "prod";

      log.debug("Sentry - initializing.");
      Sentry.init({
        dsn: env.sentryDsn,
        environment: env.name,
        release: process.env.REACT_APP_VERSION || "unknown",
        integrations: [
          Sentry.replayIntegration({
            maskAllText: false, // Disable text masking for regular text
            maskAllInputs: true, // Mask all input fields
            blockAllMedia: false // Don't block media in replays
          })
        ],
        sendDefaultPii: false,
        tracesSampleRate: 0.1, // 10% for performance monitoring across all environments
        attachStacktrace: !isProd, // Stack traces in dev/staging only; prod uses symbolicated source maps
        replaysSessionSampleRate: 0.05, // 5% session replay sampling (all environments)
        replaysOnErrorSampleRate: 0.1, // 10% replay on errors
        beforeSend(event, hint) {
          // Skip only the replay (not the trace) for auth errors that were captured with "info" level
          // in Apollo error link's Sentry.captureException() call
          const source = event.tags?.source as string;
          const level = event.level;

          // Remove replay data for Apollo network errors marked as "info" (401 auth errors)
          // but keep the trace for performance monitoring
          if (source === "apollo-network" && level === "info") {
            (event as any).replay = []; // Remove replay data only, keep event
          }

          return event;
        }
      });
      sentryInitialized = true;
      log.debug("Sentry - initialized.");
    } else if (featureFlagsInitialized && !sentryEnabled) {
      log.debug("Sentry - disabled by feature flag");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlagsInitialized, sentryEnabled]);

  // Set user context when authenticated
  useEffect(() => {
    if (sentryInitialized && sentryEnabled) {
      if (isAuthenticated && user?.sub) {
        Sentry.setUser({
          id: user.sub,
          username: user.name,
          email: user.email
        });

        // Set tags for additional context
        Sentry.setTag("isAdmin", isAdmin?.toString() || "false");
        Sentry.setTag("isSuperUser", isSuperUser?.toString() || "false");
        if (tenantId) {
          Sentry.setTag("tenantId", tenantId);
          if (tenant?.shortName) {
            Sentry.setTag("tenantName", tenant.shortName);
          }
        }

        log.debug(`Sentry - User context set for ${user.name}`);
      } else if (!isAuthenticated) {
        // Clear Sentry user context on logout
        Sentry.setUser(null);
      }
    }
  }, [isAuthenticated, user, isAdmin, isSuperUser, tenantId, tenant, sentryEnabled]);

  return <div>{children}</div>;
};

export default SentryProvider;
